// The small-reset (death) loop and the skill tree. Losing a wave ends the run,
// converts the run's reach into permanent skill points, and opens the skill
// screen. Spending points there strengthens every future run, then the player
// restarts on the same planet with those bonuses applied.

import type { GameState } from '@core/state';
import { createRun } from '@core/state';
import { refreshDerived } from '@core/state';
import { BALANCE } from '@data/balance';
import { SKILL_BY_ID, skillCost, skillUnlocked, computeMetaPerks, type SkillDef } from '@data/skills';
import { conquestAge, cosmicGain, planetName } from '@data/planets';
import { bus } from '@core/events';
import { t } from '@i18n/i18n';
import { resetHarvest } from './harvest';
import { resetBuildCooldown } from './buildings';

/** Skill points earned for how far a run reached. */
function computeSkillPoints(state: GameState): number {
  const p = BALANCE.prestige;
  const raw = state.run.age * p.pointsPerAge + state.run.wave * p.pointsPerWaves;
  return Math.max(p.minPointsOnDeath, Math.floor(raw));
}

/** Ends the run after a lost wave and opens the skill-point screen. */
export function killRun(state: GameState, wave: number): void {
  const earned = computeSkillPoints(state);
  state.meta.skillPoints += earned;
  state.meta.totalRuns += 1;
  state.pending.dead = true;
  state.pending.retired = false;
  state.pending.deathWave = wave;
  state.pending.deathSkillPoints = earned;
  bus.emit('death', { wave, skillPointsEarned: earned });
}

/** Whether the voluntary "end the civilization" retire is unlocked: the run
 *  must have at least matched the deepest age this line has EVER reached, so a
 *  record run cannot be abandoned early for cheap resets. A fresh account
 *  (bestAge 0) may retire from the start; every record raises the bar. */
export function canRetire(state: GameState): boolean {
  return state.run.age >= state.meta.bestAge;
}

/** Ends the run BY CHOICE and opens the skill screen. Unlike a death there is
 *  no pity floor: retirement pays exactly what the run earned (so idling at
 *  the gate cannot farm the minimum-points floor). */
export function retireRun(state: GameState): boolean {
  if (!canRetire(state)) return false;
  const p = BALANCE.prestige;
  const earned = Math.floor(state.run.age * p.pointsPerAge + state.run.wave * p.pointsPerWaves);
  state.meta.skillPoints += earned;
  state.meta.totalRuns += 1;
  state.pending.dead = true;
  state.pending.retired = true;
  state.pending.deathWave = state.run.wave;
  state.pending.deathSkillPoints = earned;
  bus.emit('death', { wave: state.run.wave, skillPointsEarned: earned });
  return true;
}

/** Attempts to buy one level of a skill. Returns true on success. */
export function buySkill(state: GameState, skillId: string): boolean {
  const def: SkillDef | undefined = SKILL_BY_ID[skillId];
  if (!def) return false;
  const lvl = state.meta.skillLevels[skillId] ?? 0;
  if (lvl >= def.maxLevel) return false;
  // Unlockable tree: cannot START a gated skill until its prerequisite is met.
  // Already-owned skills (lvl > 0, e.g. grandfathered saves) keep leveling.
  if (lvl === 0 && !skillUnlocked(def, state.meta.skillLevels)) return false;
  const cost = skillCost(def, lvl);
  if (state.meta.skillPoints < cost) return false;

  state.meta.skillPoints -= cost;
  state.meta.skillLevels[skillId] = lvl + 1;
  refreshDerived(state); // metaPerks now reflect the new skill level
  return true;
}

/** Marks the planet conquerable once its age (era-gate) goal is reached (called
 *  after an age advance). Conquering unlocks planetary prestige. */
export function checkConquest(state: GameState): void {
  if (state.run.conquered) return;
  if (state.run.age >= conquestAge(state.meta.planet)) {
    state.run.conquered = true;
    bus.emit('conquer:available', { planet: state.meta.planet, wave: state.run.wave });
    bus.emit('milestone', {
      title: t('milestone.conquerReady', { planet: planetName(state.meta.planet) }),
      subtitle: t('milestone.conquerReadySub'),
      tone: 'epic',
    });
  }
}

/** The big reset: conquer the planet for cosmic currency, then move on to a
 *  harder world. Skill tree and cosmic carry across; the run resets. */
export function planetaryPrestige(state: GameState): boolean {
  if (!state.run.conquered) return false;

  const gain = cosmicGain(state.meta.planet, state.run.age);
  state.meta.cosmic += gain;
  state.meta.planet += 1;
  state.meta.planetsConquered += 1;

  // Fresh run on the new (harder) planet, with cosmic + skills carrying over.
  const perks = computeMetaPerks(state.meta.skillLevels, BALANCE.offline.baseCapHours);
  state.metaPerks = perks;
  state.run = createRun(perks, state.gameTime);
  state.pending.dead = false;
  state.pending.retired = false;
  state.pending.cardChoices = null;
  resetHarvest();
  resetBuildCooldown();

  bus.emit('prestige', {
    planet: state.meta.planet,
    cosmicGain: gain,
    cosmicTotal: state.meta.cosmic,
  });
  bus.emit('milestone', {
    title: t('milestone.ascended', { planet: planetName(state.meta.planet) }),
    subtitle: t('milestone.ascendedSub', { gain }),
    tone: 'epic',
  });
  return true;
}

/** Starts a fresh run with current permanent perks applied. */
export function restartRun(state: GameState): void {
  const perks = computeMetaPerks(state.meta.skillLevels, BALANCE.offline.baseCapHours);
  state.metaPerks = perks;
  state.run = createRun(perks, state.gameTime);
  state.pending.dead = false;
  state.pending.retired = false;
  state.pending.cardChoices = null;
  state.pending.deathWave = 0;
  state.pending.deathSkillPoints = 0;
  resetHarvest();
  resetBuildCooldown();
  bus.emit('restart', { skillPoints: state.meta.skillPoints });
}
